import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUserStore } from "store/userStore";
import { useCreditStore } from "store/creditStore";
import { getFlightRequests } from "apis/requestServices";
import CreditPurchaseModal from "pages/Credits/CreditPurchaseModal";
import TokensIcon from "assets/images/tokens.png";
import StandbyLogo from "assets/images/standby.png";
import LdActive from "assets/images/ld-active.png";
import Ld from "assets/images/ld.png";
import YrqActive from "assets/images/yrq-active.png";
import Yrq from "assets/images/yrq.png";
import ReqIcon from "assets/images/req.png";
import OpenActive from "assets/images/open-active.png";
import OpenIcon from "assets/images/open.png";
import AnsweredActive from "assets/images/answered-active.png";
import AnsweredIcon from "assets/images/answered.png";
import LiveTrackingIcon from "assets/images/LiveTracking.png";
import LiveTrackingActiveIcon from "assets/images/LiveTracking-1.png";
import TokenIcon from "assets/images/token.png";
import TokenActiveIcon from "assets/images/token-active.png";
import { isAdminRole, isLifetimeOgUser, isUserSideRole } from "utils/roles";
import { getLatestContests } from "apis/adminServices/giveawayServices";
import { GiveawayContest } from "types/giveaway";
import GiveawayContestModal from "components/GiveawayContestModal";
import { Trophy, SlidersHorizontal } from "lucide-react";
import { toDate } from "pages/Admin/giveawayManagement/giveawayFormUtils";
import { InstagramIcon, LinkedInIcon } from "components/common/SocialIcons";
import SidebarLeftIcon from "assets/images/sidebar-left.svg";
import { LifetimeOgTokenCard } from "components/OgBadges";

const GiveawayCardBg = "/images/card-bg-giveaway.png";



interface SidebarProps {
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isMobileOpen: propIsMobileOpen,
  onCloseMobile,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useUserStore((state) => state.role);

  const [isRequestsOpen, setIsRequestsOpen] = useState(false);
  const [openRequestCount, setOpenRequestCount] = useState(0);
  const availableCredits = useCreditStore((s) => s.availableCredits);
  const refreshCredits = useCreditStore((s) => s.refresh);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  const isMobileOpen =
    propIsMobileOpen !== undefined ? propIsMobileOpen : internalMobileOpen;
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Publish the desktop sidebar width so the content column beside it can grow
  // when the sidebar collapses. The aside is fixed-position, so the layout
  // can't derive the width from the flow.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-w",
      isCollapsed ? "84px" : "220px"
    );
    return () => {
      document.documentElement.style.removeProperty("--sidebar-w");
    };
  }, [isCollapsed]);

  const handleClose = () => {
    if (onCloseMobile) {
      onCloseMobile();
    } else {
      setInternalMobileOpen(false);
    }
  };

  const [latestContests, setLatestContests] = useState<GiveawayContest[]>([]);
  const [isContestModalOpen, setIsContestModalOpen] = useState(false);

  // Auto-expand Requests submenu if on a request-related page
  useEffect(() => {
    if (location.pathname === "/my-requests") {
      setIsRequestsOpen(true);
    }
  }, [location.pathname]);

  // Close mobile sidebar on any navigation. Keyed on location.key (not just
  // pathname) so it also fires for Open ↔ Answered, which share the
  // /my-requests path and differ only by location.state.tab.
  useEffect(() => {
    handleClose();
  }, [location.key]);

  useEffect(() => {
    if (isUserSideRole(userRole)) {
      refreshCredits();
    }
  }, [userRole]);

  // Keep the open-request count badge fresh. Re-fetch on navigation so it
  // updates after a request is answered elsewhere in the app.
  useEffect(() => {
    if (!isUserSideRole(userRole)) return;
    let active = true;
    // Use the same list endpoint (and "open" status filter) the Open Requests
    // page itself uses, so the badge always matches what that page shows —
    // the separate /Request/OpenRequestCount endpoint could disagree and lag.
    getFlightRequests({
      pageNumber: 1,
      pageSize: 1,
      status: "open",
      sortBy: "DepartureTime",
    })
      .then((res: any) => {
        if (active) setOpenRequestCount(Number(res?.totalCount) || 0);
      })
      .catch(() => {
        /* badge is non-critical; ignore fetch errors */
      });
    return () => {
      active = false;
    };
  }, [userRole, location.pathname]);

  // Fetch latest active/upcoming contest for user sidebar
  useEffect(() => {
    if (!isUserSideRole(userRole)) return;
    let active = true;
    getLatestContests()
      .then((contests) => {
        if (!active) return;
        if (contests && contests.length > 0) {
          const validContests = contests.filter(
            (c) =>
              c.status === "Ongoing" ||
              c.status === "Upcoming" ||
              c.status === "Awaiting Result",
          );

          // Prefer the Ongoing contest with the nearest End Date; if none are
          // Ongoing, fall back to the Upcoming contest with the nearest Start Date.
          const ongoing = validContests
            .filter((c) => c.status === "Ongoing")
            .sort(
              (a, b) =>
                (toDate(a.endDate)?.getTime() ?? Infinity) -
                (toDate(b.endDate)?.getTime() ?? Infinity),
            );
          const upcoming = validContests
            .filter((c) => c.status === "Upcoming")
            .sort(
              (a, b) =>
                (toDate(a.startDate)?.getTime() ?? Infinity) -
                (toDate(b.startDate)?.getTime() ?? Infinity),
            );

          const primary = ongoing[0] ?? upcoming[0];
          const rest = validContests.filter(
            (c) => c.contestId !== primary?.contestId,
          );

          setLatestContests(
            primary
              ? [primary, ...rest]
              : validContests.length > 0
                ? validContests
                : [contests[0]],
          );
        } else {
          setLatestContests([]);
        }
      })
      .catch((err) => {
        console.error("Failed to load latest giveaway contests:", err);
      });

    return () => {
      active = false;
    };
  }, [userRole]);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center w-full h-[40px] px-[10px] gap-[10px] rounded-[6px] text-[14px] whitespace-nowrap transition-all duration-200 ${
      isCollapsed ? "md:justify-center md:px-0" : ""
    } ${
      isActive
        ? "text-[#1F2937] font-semibold bg-[#FFF4D4]"
        : "text-[#727685] hover:bg-[#F3F4F6] hover:text-[#1F2937] font-medium"
    }`;

  const navIconWrapClass = (isActive: boolean) =>
    `w-9 h-9 flex items-center justify-center shrink-0 rounded-xl transition-colors duration-200 ${
      isActive ? "bg-[#FDECD1]" : ""
    }`;

  const subNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 pl-11 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
      isActive
        ? "bg-[#FEF3C7] text-[#D97706] font-medium"
        : "text-[#727685] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
    }`;

  // `icon` is an st-* icon-font class. Routes whose icon isn't in that font
  // pass `iconNode` instead (a lucide element), which takes precedence.
  const adminRoutes: {
    to: string;
    icon?: string;
    iconNode?: React.ReactNode;
    label: string;
  }[] = [
    { to: "/admin/dashboard", icon: "st-dashboard", label: "Dashboard" },
    { to: "/admin/user-management", icon: "st-user", label: "User Management" },
    {
      to: "/admin/credit-management",
      icon: "st-credits",
      label: "Token Management",
    },
    {
      to: "/admin/report&feedback",
      icon: "st-comment",
      label: "Report & Feedback",
    },
    {
      to: "/admin/discount-management",
      icon: "st-discount",
      label: "Discount Management",
    },
    {
      to: "/admin/non-rev-config",
      iconNode: <SlidersHorizontal className="w-5 h-5" />,
      label: "Non-Rev Config",
    },
    { to: "/admin/settings", icon: "st-settings", label: "Settings" },
    {
      to: "/admin/giveaway-management",
      iconNode: <Trophy className="w-5 h-5" />,
      label: "Giveaway Management",
    },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={handleClose}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          top: "var(--giveaway-banner-h)",
          height: "calc(100% - var(--giveaway-banner-h))",
        }}
        className={`fixed left-0 ${
          isCollapsed ? "md:w-[84px]" : "w-[220px]"
        } w-[220px] bg-white md:border-r border-[#E5E7EB] flex flex-col z-40 transition-all duration-300 ${
          isMobileOpen
            ? "translate-x-0 shadow-2xl visible"
            : "-translate-x-full invisible md:visible md:translate-x-0 md:shadow-none pointer-events-none md:pointer-events-auto"
        }`}
      >
        {/* Logo */}
        {/* Logo */}
<div className="px-6 py-5 flex items-center">
  <NavLink
    to={isAdminRole(userRole) ? "/admin/dashboard" : "/search"}
    className="flex items-center gap-2 min-w-0"
  >
    {!isCollapsed && (
      <img
        src={StandbyLogo}
        alt="Standby"
        className="h-8 w-auto object-contain shrink-0"
      />
    )}

    <span
      className={`text-[#1F2937] font-bold text-xl tracking-tight ${
        isCollapsed ? "md:hidden" : ""
      }`}
    >
      STANDBY
    </span>
  </NavLink>

  {/* Collapse toggle — desktop only */}
  <button
    type="button"
    onClick={() => setIsCollapsed((prev) => !prev)}
    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
    className="hidden md:!flex shrink-0 ml-auto w-8 h-8 items-center justify-center opacity-60 hover:opacity-100 transition-opacity duration-200"
  >
    <img
      src={SidebarLeftIcon}
      alt="Collapse sidebar"
      className={`w-6 h-6 object-contain transition-transform duration-200 ${
      isCollapsed ? "scale-x-[-1]" : ""
    }`}
    />
  </button>

  {/* Back button — mobile only */}
  <button
    onClick={handleClose}
    className="md:hidden shrink-0 ml-auto w-8 h-8 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity duration-200"
    type="button"
    aria-label="Back"
  >
    <img
      src={SidebarLeftIcon}
      alt="Back"
      className="w-6 h-6 object-contain"
    />
  </button>
</div>

        {/* Navigation */}
        <nav
          className="flex-1 overflow-y-auto px-1 py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          
          {isAdminRole(userRole) ? (
            <div className="space-y-1">
              {adminRoutes.map(({ to, icon, iconNode, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={navLinkClass}
                  title={isCollapsed ? label : undefined}
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    {iconNode ? (
                      iconNode
                    ) : (
                      <i className={`${icon} text-lg leading-none`}></i>
                    )}
                  </span>
                  {!isCollapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          ) : (
            <div className="flex flex-col min-h-full">
              <div className="space-y-1">
              {/* Loads */}
                <NavLink
                  to="/search"
                  className={navLinkClass}
                  end={false}
                  title={isCollapsed ? "Loads" : undefined}
                >
                  {({ isActive }) => (
                    <>
                      <span className={navIconWrapClass(isActive)}>
                        <img
                          src={isActive ? LdActive : Ld}
                          alt="Loads"
                          className="w-5 h-5"
                        />
                      </span>
                      {!isCollapsed && <span>Loads</span>}
                    </>
                  )}
                </NavLink>

                {/* Your Requests */}
                <NavLink
                  to="/my-loads"
                  className={navLinkClass}
                  title={isCollapsed ? "Your Requests" : undefined}
                >
                  {({ isActive }) => (
                    <>
                      <span className={navIconWrapClass(isActive)}>
                        <img
                          src={isActive ? YrqActive : Yrq}
                          alt="Your Requests"
                          className="w-5 h-5"
                        />
                      </span>
                      {!isCollapsed && <span>Your Requests</span>}
                    </>
                  )}
                </NavLink>

                {/* Requests (collapsible) */}
                <div>
                  <button
                    onClick={() => {
                      setIsRequestsOpen(true);
                      navigate("/my-requests", { state: { tab: "open" } });
                    }}
                    className={`flex items-center px-[10px] w-full h-[40px] gap-[10px] rounded-[6px] text-[14px] transition-all duration-200 ${
                      isCollapsed
                        ? "md:justify-center md:px-0"
                        : "justify-between"
                    } text-[#727685] hover:bg-[#F3F4F6] hover:text-[#1F2937] font-medium`}
                    type="button"
                    title={isCollapsed ? "Requests" : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <span className={navIconWrapClass(false)}>
                        <img src={ReqIcon} alt="Requests" className="w-5 h-5" />
                      </span>
                      {!isCollapsed && <span>Requests</span>}
                      {!isCollapsed && openRequestCount > 0 && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-[#FBBF24] text-black">
                          {openRequestCount}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${isRequestsOpen ? "rotate-180" : ""} text-[#727685]`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    )}
                  </button>

                  {!isCollapsed && isRequestsOpen && (
                    <div className="mt-1 mx-2 py-1  rounded-[6px] bg-[#F9FAFB]">
                      <NavLink
                        to="/my-requests"
                        state={{ tab: "open" }}
                        className={({ isActive }) =>
                          `flex items-center  pl-[10px] pr-4 w-full h-[40px]  gap-[10px] rounded-[6px] text-[14px] transition-all duration-200 ${
                            isActive && location.state?.tab !== "answered"
                              ? "text-[#1F2937] font-semibold bg-[#FFF4D4]"
                              : "text-[#727685] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span className="w-9 h-9 flex items-center justify-center shrink-0">
                              <img
                                src={
                                  isActive && location.state?.tab !== "answered"
                                    ? OpenActive
                                    : OpenIcon
                                }
                                alt="Open"
                                className="w-4 h-4"
                              />
                            </span>
                            <span>Open</span>
                          </>
                        )}
                      </NavLink>
                      <NavLink
                        to="/my-requests"
                        state={{ tab: "answered" }}
                        className={({ isActive }) =>
                          `flex items-center  pl-[10px] pr-4 w-full h-[40px]  gap-[10px] rounded-[6px] text-[14px] transition-all duration-200 ${
                            isActive && location.state?.tab === "answered"
                              ? "text-[#1F2937] font-semibold bg-[#FFF4D4]"
                              : "text-[#727685] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span className="w-9 h-9 flex items-center justify-center shrink-0">
                              <img
                                src={
                                  isActive && location.state?.tab === "answered"
                                    ? AnsweredActive
                                    : AnsweredIcon
                                }
                                alt="Answered"
                                className="w-4 h-4"
                              />
                            </span>
                            <span>Answered</span>
                          </>
                        )}
                      </NavLink>
                    </div>
                  )}
                </div>

                {/* Live Tracking */}
                <NavLink
                  to="/live-tracking"
                  className={navLinkClass}
                  title={isCollapsed ? "Flight Status" : undefined}
                >
                  {({ isActive }) => (
                    <>
                      <span className={navIconWrapClass(isActive)}>
                        <img
                          src={
                            isActive ? LiveTrackingActiveIcon : LiveTrackingIcon
                          }
                          alt="Flight Status"
                          className="w-5 h-5"
                        />
                      </span>
                      {!isCollapsed && <span>Flight Status</span>}
                    </>
                  )}
                </NavLink>

                {/* Deals
                <NavLink to="/deals" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <svg
                        className={`w-5 h-5 ${isActive ? "text-[#1F2937]" : "text-[#9CA3AF]"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 014 7V4a1 1 0 011-1z"
                        />
                      </svg>
                      <span>Deals</span>
                    </>
                  )}
                </NavLink> */}

                {/* Tokens tab */}
                <NavLink
                  to="/credits"
                  className={navLinkClass}
                  title={isCollapsed ? "Tokens" : undefined}
                >
                  {({ isActive }) => (
                    <>
                      <span className={navIconWrapClass(isActive)}>
                        <img
                          src={isActive ? TokenActiveIcon : TokenIcon}
                          alt="Tokens"
                          className="w-5 h-5"
                        />
                      </span>
                      {!isCollapsed && <span>Tokens</span>}
                    </>
                  )}
                </NavLink>
              </div>

              {/* Token Balance Card - only for user role, hidden on credits page and
                    while collapsed. Lifetime OG users see an "Unlimited Tokens" card
                    instead of their numeric balance; everyone else sees the balance
                    plus a purchase CTA. */}
              <div className="mt-auto px-0 ">
                {!isCollapsed &&
                  isUserSideRole(userRole) &&
                  location.pathname !== "/credits" &&
                  (isLifetimeOgUser(userRole) ? (
                    <div className="mt-2 mx-2">
                      <LifetimeOgTokenCard />
                    </div>
                  ) : (
                    <div className="mb-2 mt-2 mx-2 bg-gradient-to-br from-[#1F2937] to-[#111827] rounded-2xl p-2">
                      <div className="flex items-center gap-3 mb-2">
                        <img
                          src={TokensIcon}
                          alt="Tokens"
                          className="w-11 h-11 ml-1 object-contain"
                        />
                        <div>
                          <p className="text-white text-2xl font-bold leading-none">
                            {availableCredits == null ? "..." : availableCredits}
                          </p>
                          <p className="text-white text-[11px] mt-0.5 font-manrope">
                            Token Balance
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowCreditPurchaseModal(true)}
                        className="w-full bg-[#FBBF24] text-black text-sm font-semibold py-2.5 rounded-xl hover:bg-[#F59E0B] transition-colors"
                      >
                        Purchase Tokens
                      </button>
                    </div>
                  ))}

                {!isCollapsed &&
                  isUserSideRole(userRole) &&
                  location.pathname !== "/credits" &&
                  latestContests.length > 0 && (
                    <div className="mt-3 mx-2 border-t border-[#E5E7EB]" />
                  )}

                {/* Active Giveaway Contest Card */}
                {!isCollapsed && latestContests.length > 0 && (
                  <div
                    onClick={() => setIsContestModalOpen(true)}
                    className="mt-2 mx-2 relative overflow-hidden p-3 rounded-2xl bg-[#4338CA] bg-cover bg-center cursor-pointer shadow-md hover:shadow-violet-950/50 hover:scale-[1.02] transition-all group"
                    style={{ backgroundImage: `url(${GiveawayCardBg})` }}
                  >
                    <div className="relative z-10 max-w-[62%]">
                      <p className="text-white text-sm font-bold leading-tight line-clamp-2">
                        {latestContests[0].contestName}
                        {latestContests.length > 1 && (
                          <span className="text-[#FBBF24] font-black">
                            {" "}
                            +{latestContests.length - 1} more
                          </span>
                        )}
                      </p>
                      <p className="text-white/70 text-[10px] mt-1 line-clamp-2 leading-tight">
                        Participate now and stand a chance to win exciting prizes!
                      </p>
                      <span className="inline-flex items-center gap-1 mt-3 bg-[#FBBF24] text-[#3B0764] text-[11px] font-bold px-3 py-1.5 rounded-lg group-hover:bg-[#F59E0B] transition-colors">
                        Join Now
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </span>
                    </div>

                    <div className="absolute top-3 right-3 flex items-center gap-1.5 text-white/80 z-10">
                      <InstagramIcon className="w-3.5 h-3.5" />
                      <LinkedInIcon className="w-3.5 h-3.5" />
                    </div>

                    
                  </div>
                )}

              </div>
            </div>
          )}
        </nav>

      </aside>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        onPaymentComplete={refreshCredits}
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
      />

      {/* Giveaway Contest Details Modal */}
      <GiveawayContestModal
        isOpen={isContestModalOpen}
        onClose={() => setIsContestModalOpen(false)}
        contests={latestContests}
      />
    </>
  );
};

export default Sidebar;
